/**
 * 費用明細卡片 - 前後台共用元件
 * 資料來源統一由 order module 計算
 */
import { formatCurrency } from '../../modules/order/helpers';
import type { OrderCostSummary } from '../../modules/order/types';

interface CostSummaryCardProps {
  summary: OrderCostSummary;
  /** 是否為後台樣式（有邊框） */
  isAdmin?: boolean;
}

export default function CostSummaryCard({ summary, isAdmin = false }: CostSummaryCardProps) {
  const wrapperClass = isAdmin
    ? 'bg-gray-900 rounded-lg shadow-sm border border-gray-700 p-6'
    : 'bg-white rounded-lg shadow-sm p-6';

  return (
    <div className={wrapperClass}>
      <h2 className={`text-${isAdmin ? 'xl' : 'lg'} font-bold ${isAdmin ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
        <i className={`ri-money-dollar-circle-line ${isAdmin ? 'text-amber-400' : ''}`}></i>
        費用明細
      </h2>
      <div className="space-y-3">
        {/* 器材小計 */}
        <div className="flex items-center justify-between text-sm">
          <span className={isAdmin ? 'text-gray-400' : 'text-gray-600'}>器材小計</span>
          <span className={`font-medium ${isAdmin ? 'text-gray-100' : 'text-gray-900'}`}>
            {formatCurrency(summary.equipmentSubtotal)}
          </span>
        </div>

        {/* 天數 */}
        <div className="flex items-center justify-between text-sm">
          <span className={isAdmin ? 'text-gray-400' : 'text-gray-600'}>天數</span>
          <span className={`font-medium ${isAdmin ? 'text-gray-100' : 'text-gray-900'}`}>
            {summary.rentalDays} 天
          </span>
        </div>

        {/* 客戶折扣 - 永遠顯示 */}
        <div className="flex items-center justify-between text-sm">
          <span className={isAdmin ? 'text-gray-400' : 'text-gray-600'}>
            客戶折扣
            {summary.discountRate < 100 && (
              <span className={`ml-1 ${isAdmin ? 'text-gray-500' : 'text-gray-400'}`}>({summary.discountRate / 10}折)</span>
            )}
          </span>
          <span className={`font-medium ${isAdmin ? 'text-red-400' : 'text-red-600'}`}>
            {summary.discountAmount > 0
              ? `-${formatCurrency(summary.discountAmount)}`
              : formatCurrency(0)}
          </span>
        </div>

        {/* 🆕 加租車輛費用 */}
        {summary.vehicleFee && summary.vehicleFee > 0 && (
          <div className={`flex items-center justify-between text-sm pt-2 border-t ${isAdmin ? 'border-gray-700' : 'border-gray-100'}`}>
            <span className={`${isAdmin ? 'text-gray-400' : 'text-gray-600'} flex items-center gap-1`}>
              <i className={`ri-car-line ${isAdmin ? 'text-blue-400' : 'text-blue-600'}`}></i>
              加租車輛
            </span>
            <span className={`font-medium ${isAdmin ? 'text-blue-400' : 'text-blue-600'}`}>
              {formatCurrency(summary.vehicleFee)}
            </span>
          </div>
        )}
        {summary.vehicleFee && summary.vehicleFee > 0 && (
          <div className={`text-xs ${isAdmin ? 'text-gray-500' : 'text-gray-500'} -mt-2`}>
            油錢、過路費另計
          </div>
        )}

        {/* 分隔線 */}
        <div className={`border-t ${isAdmin ? 'border-gray-700' : 'border-gray-200'} pt-3 mt-3 space-y-3`}>
          {/* 總計 */}
          <div className="flex items-center justify-between">
            <span className={`font-semibold ${isAdmin ? 'text-white' : 'text-gray-900'}`}>總計</span>
            <span className={`text-xl font-bold ${isAdmin ? 'text-amber-400' : 'text-gray-900'}`}>
              {formatCurrency(summary.finalTotal)}
            </span>
          </div>

          {/* 押金 */}
          <div className="flex items-center justify-between">
            <span className={isAdmin ? 'text-gray-400' : 'text-gray-600'}>押金</span>
            <span className={`font-semibold ${isAdmin ? 'text-emerald-400' : 'text-green-600'}`}>
              {formatCurrency(summary.totalDeposit)}
            </span>
          </div>
          
          {/* 押金提示文字 */}
          <div className={`text-xs rounded px-2 py-1.5 -mt-1 ${isAdmin ? 'text-amber-300 bg-amber-900/30' : 'text-amber-600 bg-amber-50'}`}>
            <i className="ri-information-line mr-1"></i>
            押金以現場狀況為主，此處僅作提示作用。實際工作上，工作人員會依客戶狀況調整押金。
          </div>
        </div>
      </div>
    </div>
  );
}
